import type {
  CustomEventRuntimeEntry,
  FunctionRuntimeEntry,
  SceneVariableOverride,
  VariableOverrideApi,
  VariableRuntimeEntry,
} from "../../../util/src/blueprint/runtime/types.js";

type DrainSubscriber = () => void;
type SubscriberEntry = {
  callback: DrainSubscriber;
  nextWakeAt?: number;
};

export type BehaviorDrainSubscription = {
  unsubscribe: () => void;
  setNextWakeAt: (nextWakeAt?: number) => void;
};

const isBehaviorDrainProfilingEnabled = (): boolean => {
  if (typeof window === "undefined") return false;
  return Boolean(
    (window as Window & { __MM_BEHAVIOR_DRAIN_PROFILING__?: boolean })
      .__MM_BEHAVIOR_DRAIN_PROFILING__,
  );
};

/**
 * Coordinates behavior runtime drains across all rendered nodes.
 *
 * Instead of each hook draining immediately on data updates, subscribers
 * request a global flush. The coordinator batches these requests into one
 * microtask so all behavior graphs are drained in a single pass.
 *
 * The coordinator also holds a transient batch of Control Room signals
 * (normalized shortcut combos) that are consumed during the next flush
 * and cleared immediately after.
 */
export class BehaviorDrainCoordinator {
  private subscribers = new Map<number, SubscriberEntry>();
  private nextId = 1;
  private pendingFlush = false;
  private wakeTimer: ReturnType<typeof setTimeout> | null = null;
  private flushCount = 0;
  private lastFlushStartedAt = 0;
  private _pendingControlRoomSignals: string[] = [];
  private _customEventRuntimeEntries: ReadonlyMap<
    string,
    CustomEventRuntimeEntry
  > = new Map();
  private _functionRuntimeEntries: ReadonlyMap<string, FunctionRuntimeEntry> =
    new Map();
  private _variableRuntimeEntries: ReadonlyMap<string, VariableRuntimeEntry> =
    new Map();
  private _variableOverrides = new Map<string, SceneVariableOverride>();

  /** API object passed to ExecutionContext for `setVariable` / `variableRef`. */
  readonly variableOverrideApi: VariableOverrideApi = {
    set: (variableId: string, override: SceneVariableOverride) => {
      this._variableOverrides.set(variableId, override);
    },
    get: (variableId: string) => this._variableOverrides.get(variableId),
    has: (variableId: string) => this._variableOverrides.has(variableId),
  };

  /** Clears all variable overrides (document/meet change). */
  clearVariableOverrides(): void {
    this._variableOverrides.clear();
  }

  subscribe(subscriber: DrainSubscriber): BehaviorDrainSubscription {
    const id = this.nextId++;
    this.subscribers.set(id, { callback: subscriber });
    return {
      unsubscribe: () => {
        this.subscribers.delete(id);
        this.scheduleNextWake();
      },
      setNextWakeAt: (nextWakeAt?: number) => {
        const entry = this.subscribers.get(id);
        if (!entry) return;
        entry.nextWakeAt = nextWakeAt;
        this.scheduleNextWake();
      },
    };
  }

  /**
   * Enqueue one or more Control Room shortcut combos to be delivered to all
   * subscribers on the next drain flush, then trigger a global drain.
   */
  dispatchControlRoomSignals(combos: string[]): void {
    if (combos.length === 0) return;
    this._pendingControlRoomSignals.push(...combos);
    this.requestGlobalDrain();
  }

  /**
   * Returns and clears the pending Control Room signals.
   * Called internally by each subscriber during its drain callback.
   */
  consumeControlRoomSignals(): string[] {
    return this._pendingControlRoomSignals;
  }

  /**
   * Registers publishable custom-event trigger runtimes for `onCustomSceneEvent` nodes.
   */
  setCustomEventRuntimeEntries(
    entries: ReadonlyMap<string, CustomEventRuntimeEntry>,
  ): void {
    this._customEventRuntimeEntries = entries;
  }

  /**
   * Returns the current custom-event trigger registry (read-only for drains).
   */
  getCustomEventRuntimeEntries(): ReadonlyMap<string, CustomEventRuntimeEntry> {
    return this._customEventRuntimeEntries;
  }

  /**
   * Registers publishable function-body runtimes for `call` action nodes.
   */
  setFunctionRuntimeEntries(
    entries: ReadonlyMap<string, FunctionRuntimeEntry>,
  ): void {
    this._functionRuntimeEntries = entries;
  }

  /**
   * Returns the current function runtime registry (read-only for drains).
   */
  getFunctionRuntimeEntries(): ReadonlyMap<string, FunctionRuntimeEntry> {
    return this._functionRuntimeEntries;
  }

  setVariableRuntimeEntries(
    entries: ReadonlyMap<string, VariableRuntimeEntry>,
  ): void {
    this._variableRuntimeEntries = entries;
  }

  getVariableRuntimeEntries(): ReadonlyMap<string, VariableRuntimeEntry> {
    return this._variableRuntimeEntries;
  }

  requestGlobalDrain(): void {
    if (this.pendingFlush) return;
    this.pendingFlush = true;
    queueMicrotask(() => {
      this.pendingFlush = false;
      const profiling = isBehaviorDrainProfilingEnabled();
      const startedAt = profiling ? performance.now() : 0;
      this.flushCount += 1;
      for (const subscriber of this.subscribers.values()) {
        subscriber.callback();
      }
      this._pendingControlRoomSignals = [];
      if (profiling) {
        const durationMs = performance.now() - startedAt;
        const now = performance.now();
        const sinceLastMs =
          this.lastFlushStartedAt > 0 ? now - this.lastFlushStartedAt : 0;
        this.lastFlushStartedAt = now;
        console.debug("[behavior-drain]", {
          flush: this.flushCount,
          subscribers: this.subscribers.size,
          durationMs: Number(durationMs.toFixed(3)),
          sinceLastMs: Number(sinceLastMs.toFixed(3)),
        });
      }
      this.scheduleNextWake();
    });
  }

  private scheduleNextWake(): void {
    if (this.wakeTimer) {
      clearTimeout(this.wakeTimer);
      this.wakeTimer = null;
    }

    let minWakeAt: number | undefined;
    for (const entry of this.subscribers.values()) {
      if (entry.nextWakeAt == null) continue;
      if (minWakeAt == null || entry.nextWakeAt < minWakeAt) {
        minWakeAt = entry.nextWakeAt;
      }
    }

    if (minWakeAt == null) return;

    const delay = Math.max(0, minWakeAt - Date.now());
    this.wakeTimer = setTimeout(() => {
      this.wakeTimer = null;
      this.requestGlobalDrain();
    }, delay);
  }
}

export const behaviorDrainCoordinator = new BehaviorDrainCoordinator();
